import {
  useCommonPageSelectionBlockListQuery,
  useCommonCmpasModuleSectionListQuery,
  useCommonPageSectionListQuery,
  CmpasModuleSectionDetail,
  PageSection,
  PageSectionBlock,
  useCommonPageSelectionBlockTypeListQuery,
} from "@app/api/carApi.generated";
import { sortBySortOrder } from "@app/utils";
import { useMemo } from "react";

interface SettingsBackendPageSectionBlock extends PageSectionBlock {
  label: string;
}

interface SettingsBackendPageSection extends PageSection {
  blocks: SettingsBackendPageSectionBlock[];
}

export interface SettingsBackendPage extends CmpasModuleSectionDetail {
  sections: SettingsBackendPageSection[];
  children: SettingsBackendPage[];
}

const MAX_LEVEL = 10;

export const useSettingsBackendPages = () => {
  const pages = useCommonCmpasModuleSectionListQuery();
  const pageSections = useCommonPageSectionListQuery();
  const pageSectionBlocks = useCommonPageSelectionBlockListQuery();
  const blockTypes = useCommonPageSelectionBlockTypeListQuery();

  return useMemo(() => {
    const allItems = sortBySortOrder(
      pages.data?.map<SettingsBackendPage>((p) => ({
        ...p,
        sections: sortBySortOrder(
          pageSections.data
            ?.filter((ps) => ps.page === p.id)
            .map<SettingsBackendPageSection>((ps) => ({
              ...ps,
              blocks: sortBySortOrder(
                pageSectionBlocks.data
                  ?.filter((psb) => psb.page_section === ps.id)
                  .map<SettingsBackendPageSectionBlock>((psb) => ({
                    ...psb,
                    label:
                      blockTypes.data?.find((bt) => bt.id === psb.block_type)
                        ?.display_label ?? "",
                  })) ?? [],
              ),
            })) ?? [],
        ),
        children: [],
      })) ?? [],
    );

    const items = allItems.filter(
      (i) => !allItems.some((p) => p.id === i.parent_page),
    );
    const assignChildren = (page: SettingsBackendPage, level: number) => {
      if (level > MAX_LEVEL) {
        return;
      }

      page.children = allItems.filter((c) => c.parent_page === page.id);
      page.children.forEach((c) => assignChildren(c, level + 1));
    };

    items.forEach((p) => assignChildren(p, 0));

    return {
      isLoading:
        pages.isLoading ||
        pageSections.isLoading ||
        pageSectionBlocks.isLoading ||
        blockTypes.isLoading,
      items,
    };
  }, [pages, pageSections, pageSectionBlocks, blockTypes]);
};
